import { Badge } from "@/components/ui/badge";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { MultiSelect } from "@/components/ui/multi-select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ALLOCATION_SKILLS, SKILL_IDS } from "@/constants/allocations";
import { AllocationSkillId, CapFilter, SkillFilterMode } from "@/types/allocations";
import { format, parse } from "date-fns";
import { Download, Funnel, Plus, Search, Upload, X } from "lucide-react";
import { useState } from "react";
import { Button } from "../ui/button";
import { cn } from "@/lib/utils";

interface AllocationsFiltersProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  selectedDepartments: string[];
  onDepartmentsChange: (value: string[]) => void;
  departments: string[];
  selectedSkills: string[];
  onSkillsChange: (value: string[]) => void;
  skillFilterMode: SkillFilterMode;
  onSkillFilterModeChange: (value: SkillFilterMode) => void;
  skillOptions: { value: string; label: string }[];
  selectedStatus: string;
  onStatusChange: (value: string) => void;
  shiftDate: string;
  onShiftDateChange: (value: string) => void;
  capFilters: CapFilter[];
  onCapFiltersChange: (filters: CapFilter[]) => void;
  onUploadClick: () => void;
  onDownloadTemplateClick: () => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  hasCapsData: boolean;
}

export const AllocationsFilters = ({
  searchTerm,
  onSearchChange,
  selectedDepartments,
  onDepartmentsChange,
  departments,
  selectedSkills,
  onSkillsChange,
  skillFilterMode,
  onSkillFilterModeChange,
  skillOptions,
  selectedStatus,
  onStatusChange,
  shiftDate,
  onShiftDateChange,
  capFilters,
  onCapFiltersChange,
  onUploadClick,
  onDownloadTemplateClick,
  fileInputRef,
  onFileChange,
  hasCapsData,
}: AllocationsFiltersProps) => {
  const [pendingSkillId, setPendingSkillId] = useState<string>("");

  const shiftDateValue = shiftDate ? parse(shiftDate, "yyyy-MM-dd", new Date()) : undefined;

  const handleShiftDateChange = (date: Date | undefined) => {
    if (date) {
      onShiftDateChange(format(date, "yyyy-MM-dd"));
    }
  };

  const activeFilterCount =
    (selectedDepartments.length > 0 ? 1 : 0) +
    (selectedSkills.length > 0 ? 1 : 0) +
    (selectedStatus !== "all" ? 1 : 0) +
    capFilters.length;
  const disableSearchControls = !hasCapsData && !searchTerm;

  const handleClearFilters = () => {
    onDepartmentsChange([]);
    onSkillsChange([]);
    onSkillFilterModeChange("any");
    onStatusChange("all");
    onCapFiltersChange([]);
    setPendingSkillId("");
  };

  const usedSkillIds = new Set(capFilters.map((f) => f.skillId));
  const availableSkillIds = SKILL_IDS.filter((id) => !usedSkillIds.has(id));

  const handleAddCapFilter = () => {
    if (!pendingSkillId) return;
    const skillId = Number(pendingSkillId) as AllocationSkillId;
    onCapFiltersChange([...capFilters, { skillId }]);
    setPendingSkillId("");
  };

  const handleRemoveCapFilter = (skillId: AllocationSkillId) => {
    onCapFiltersChange(capFilters.filter((f) => f.skillId !== skillId));
  };

  const handleCapFilterChange = (skillId: AllocationSkillId, field: "min" | "max", value: string) => {
    const parsed = value === "" ? undefined : Number(value);
    onCapFiltersChange(capFilters.map((f) => (f.skillId === skillId ? { ...f, [field]: parsed } : f)));
  };

  const handleSkillFilterModeChange = (value: string) => {
    if (value === "all" || value === "any") {
      onSkillFilterModeChange(value);
    }
  };

  return (
    <div className="flex flex-col gap-3 border-b px-4 py-3 md:flex-row md:items-center md:justify-between md:gap-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
        <div className="relative max-w-sm flex-1">
          <Search
            className={cn(
              "text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2",
              disableSearchControls && "opacity-50"
            )}
          />
          <Input
            disabled={disableSearchControls}
            placeholder="Search agents..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <Button disabled={disableSearchControls} variant="outline">
              <Funnel className="text-muted-foreground size-3.5" />
              Filters
              {activeFilterCount > 0 && <Badge className="ml-1 h-5 min-w-5 px-1.5">{activeFilterCount}</Badge>}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-96 p-0">
            <div className="flex flex-col gap-4 p-3">
              <Tabs value={selectedStatus} onValueChange={onStatusChange}>
                <TabsList className="w-full">
                  <TabsTrigger value="all" className="flex-1">
                    All
                  </TabsTrigger>
                  <TabsTrigger value="receivingLeads" className="flex-1">
                    Active
                  </TabsTrigger>
                  <TabsTrigger value="notReceivingLeads" className="flex-1">
                    Inactive
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium">Departments</label>
                <MultiSelect
                  options={departments.map((dept) => ({
                    value: dept,
                    label: dept,
                  }))}
                  defaultValue={selectedDepartments}
                  onValueChange={onDepartmentsChange}
                  placeholder="All Departments"
                  showBadges={false}
                  searchable={false}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium">Skills</label>
                <MultiSelect
                  options={skillOptions}
                  defaultValue={selectedSkills}
                  onValueChange={onSkillsChange}
                  placeholder="All Skills"
                  showBadges={false}
                  searchable={false}
                />
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground text-xs">Agents with ANY/ALL of the selected skills.</span>
                  <Tabs value={skillFilterMode} onValueChange={handleSkillFilterModeChange}>
                    <TabsList className="w-24">
                      <TabsTrigger value="any" className="w-12 px-0">
                        ANY
                      </TabsTrigger>
                      <TabsTrigger value="all" className="w-12 px-0">
                        ALL
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium">Caps</label>
                {capFilters.map((filter) => (
                  <div key={filter.skillId} className="flex items-center gap-2">
                    <div className="flex h-9 grow items-center truncate rounded-lg">
                      <span className="truncate text-sm">{ALLOCATION_SKILLS[filter.skillId]}</span>
                    </div>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      placeholder="Min"
                      value={filter.min ?? ""}
                      onChange={(e) => handleCapFilterChange(filter.skillId, "min", e.target.value)}
                      className="w-16 text-sm"
                    />
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      placeholder="Max"
                      value={filter.max ?? ""}
                      onChange={(e) => handleCapFilterChange(filter.skillId, "max", e.target.value)}
                      className="w-16 text-sm"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      className="shrink-0"
                      onClick={() => handleRemoveCapFilter(filter.skillId)}
                    >
                      <X className="size-3.5 text-rose-700" />
                    </Button>
                  </div>
                ))}
                {availableSkillIds.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Select value={pendingSkillId} onValueChange={setPendingSkillId}>
                      <SelectTrigger className="grow text-sm">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent position="popper">
                        {availableSkillIds.map((id) => (
                          <SelectItem key={id} value={String(id)}>
                            {ALLOCATION_SKILLS[id]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="icon"
                      className="shrink-0"
                      onClick={handleAddCapFilter}
                      disabled={!pendingSkillId}
                    >
                      <Plus className="size-3.5" />
                    </Button>
                  </div>
                )}
              </div>
              {activeFilterCount > 0 && (
                <Button variant="ghost" className="w-full" onClick={handleClearFilters}>
                  Clear Filters
                </Button>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex w-full items-center gap-3 [&>button]:basis-1/3 md:[&>button]:basis-auto">
          <Button variant="outline" onClick={onDownloadTemplateClick}>
            <Download className="text-muted-foreground size-3.5" size={16} />
            Download Template
          </Button>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={onFileChange} className="hidden" />
          <Button variant="outline" onClick={onUploadClick}>
            <Upload className="text-muted-foreground size-3.5" size={16} />
            Import Caps
          </Button>
        </div>
        <DatePicker
          value={shiftDateValue}
          onValueChange={handleShiftDateChange}
          id="allocations-shift-date"
          className="w-full md:w-fit"
        />
      </div>
    </div>
  );
};
